import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface CasheaBox {
    name: string;
    uuid: string;
}

export interface CasheaCreateOrderResponse {
    uuid?: string;
    idNumber: number;
}

export interface CasheaPaymentPlanResponse {
    downPayment: number;
    financedAmount: number;
    downPaymentStatus: 'PENDING' | 'PAID';
}

/**
 * Con cuál de los dos productos de Cashea se originó una orden. QR y Link son
 * APIs separadas: el backend necesita saber cuál es para consultarle la orden a
 * la que corresponde. Si no se manda, asume QR (órdenes anteriores a Link).
 */
export type CasheaOrderKind = 'QR' | 'LINK';

/**
 * Una orden de Cashea ya normalizada por el backend. Es la misma forma para los
 * dos productos: las diferencias entre el QR y Link se resuelven del lado del
 * servidor. Los campos que un producto no entrega llegan en `null` (Link no
 * devuelve ni `createdAt` ni `invoiceId`).
 */
export interface CasheaOrderDetails {
    kind: CasheaOrderKind;
    reference: string;
    status: string;
    amount: number | null;
    downPaymentAmount: number | null;
    financedAmount: number | null;
    downPaymentPaidAt: string | null;
    createdAt: string | null;
    invoiceId: string | null;
    customer: { identificationNumber: string | null; fullName: string | null };
    installments: Array<{
        id: string;
        installmentNumber: number;
        scheduledPaymentDate: string;
        amount: number;
        status: string;
    }>;
}

/** Venta cubierta por un pago agregado de Cashea (transferencia semanal o liquidación mensual). */
export interface CasheaPayoutItem {
    _id: string;
    name?: string;
    subjectName?: string;
    amount: number;
    openingAmount?: number;
    /** Monto que este pago cubrió de esta cuenta (la suma del detalle cuadra con el movimiento). */
    coveredUSD: number;
    correlationId?: string;
    createdAt?: string;
    bill?: { _id: string; order?: number; orderStr?: string; totalPriceUSD?: number; casheaOrderId?: string; customerData?: any; createdAt?: string };
}

/* ---------------------------------------------------------------- *
 * Cashea Link (pago remoto)
 * ---------------------------------------------------------------- */

export type CasheaLinkStatus = 'PENDING_CUSTOMER' | 'PENDING_DOWNPAYMENT' | 'READY' | 'CANCELLED';

/** Orden de Cashea Link tal como la lista la pestaña del POS. */
/** Una línea del carrito de la orden; `unitPrice` es sin IVA. */
export interface CasheaLinkOrderItem {
    name: string;
    quantity: number;
    unitPrice: number;
    iva: number;
}

export interface CasheaLinkOrder {
    saleId: string;
    name: string;
    /** `open` mientras está viva; `cashea_cancelled` cuando Cashea la dio de baja. */
    saleStatus: string;
    totalPrice: number;
    itemsCount: number;
    items: CasheaLinkOrderItem[];
    customerPhone: string | null;
    createdAt: string;

    identifierNumber: number;
    url: string | null;
    orderStatus: CasheaLinkStatus;
    identificationNumber: string | null;
    customerName: string | null;
    amount: number | null;
    downPayment: number | null;
    financedAmount: number | null;
    downPaymentMode: 'IN_APP' | 'MERCHANT' | null;
    downPaymentConfirmed: boolean;
    orderCreatedAt: string | null;
    /** Cuándo suelta el stock que retiene. Null en las READY: ya están cobradas. */
    expiresAt: string | null;
    /** Cuándo quedó lista para finalizar, para avisar si lleva días sin cerrarse. */
    readyAt: string | null;
    cancelledAt: string | null;
    cancelReason: string | null;
}

/**
 * Copia del cliente de Fina que se guarda en la venta.
 *
 * Sobrevive al estacionamiento: el cajero cierra el navegador mientras el
 * cliente aprueba el plan, y al volver la factura tiene que salir a nombre de
 * quien corresponde. No se confunde con la cédula de `casheaLink`, que es la
 * de quien financia y no siempre es la misma persona.
 */
export interface CasheaLinkCustomer {
    _id: string;
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    address?: string | null;
    dniType?: string | null;
    dniNumber?: string | null;
}

export interface CasheaLinkOrders {
    orders: CasheaLinkOrder[];
}

export interface CasheaLinkPlan {
    downPayment?: number;
    financedAmount?: number;
    installments?: Array<{ installmentNumber: number; scheduledPaymentDate: string; amount: number }>;
}

export interface CasheaLinkCreated {
    saleId: string;
    identifierNumber: number;
    url: string | null;
    amount: number;
}

/**
 * Qué productos de Cashea tiene habilitados el comercio. Mismos valores por
 * defecto que la API: sin configurar, el QR sigue prendido (es lo que ya usaban
 * todos) y Link apagado, porque Cashea lo habilita comercio por comercio.
 */
export interface CasheaMethods {
    qr: boolean;
    link: boolean;
}

export const casheaMethodsOf = (tenant: any): CasheaMethods => ({
    qr: tenant?.cashea?.methods?.qr !== false,
    link: tenant?.cashea?.methods?.link === true,
});

/** Propiedades del grupo `tenant` en PostHog, para segmentar flags por los productos de Cashea que tiene el comercio. */
export const casheaGroupProps = (tenant: any): { casheaQr: boolean; casheaLink: boolean } => {
    const enabled = tenant?.cashea?.enabled === true;
    const methods = casheaMethodsOf(tenant);
    return { casheaQr: enabled && methods.qr, casheaLink: enabled && methods.link };
};

/** Lo que hace falta de una orden para armar el mensaje que se le manda al cliente. */
export interface CasheaLinkShareable {
    url: string | null;
    customerName?: string | null;
    /** Teléfono del cliente de Fina: con él, WhatsApp abre directo su chat. */
    customerPhone?: string | null;
    orderStatus: CasheaLinkStatus;
    downPayment?: number | null;
    downPaymentMode?: 'IN_APP' | 'MERCHANT' | null;
}

/**
 * Link de WhatsApp con el mensaje ya escrito, distinto según lo que le falte
 * al cliente: no es lo mismo "elige tu plan" que "ya lo aprobaste, falta la
 * inicial". Lo usan la tabla de órdenes y el modal que acaba de generar el link.
 */
export const casheaLinkWhatsAppUrl = (order: CasheaLinkShareable): string => {
    const saludo = order.customerName ? `¡Hola, ${order.customerName}!` : '¡Hola!';
    const mensaje = [saludo, casheaLinkShareMessage(order), `🔗 ${order.url}`].join('\n\n');
    const phone = toWhatsAppPhone(order.customerPhone);
    return `https://api.whatsapp.com/send?${phone ? `phone=${phone}&` : ''}text=${encodeURIComponent(mensaje)}`;
};

/**
 * El teléfono en el formato que pide WhatsApp: código de país y sin el 0, solo
 * números ("0424-987.65.44" → "584249876544"). Si no se reconoce, null: mejor
 * que el cajero elija el chat a mano que abrir uno equivocado.
 */
export const toWhatsAppPhone = (raw: string | null | undefined): string | null => {
    let digits = String(raw ?? '').replace(/\D/g, '');
    if (digits.startsWith('00')) digits = digits.slice(2);
    if (/^0\d{10}$/.test(digits)) return `58${digits.slice(1)}`;
    if (/^4\d{9}$/.test(digits)) return `58${digits}`;
    if (/^58\d{10}$/.test(digits)) return digits;
    return null;
};

export interface CasheaIntegrationPrefill {
    commerceName: string;
    rif: string;
    contactPhone?: string;
}

/** Contrato del comercio con Cashea, en porcentaje como figura en el contrato. */
export interface CasheaContractTermsInput {
    contactPhone: string;
    payoutModel: 'base' | 'express';
    techServicePercent?: number | null;
    lendingDiscountPercent?: number | null;
    contribuyenteEspecial: boolean;
    ivaRetencionPercent?: 75 | 100 | null;
}

/** Solicitud del comercio para conectar su cuenta de Cashea a Fina. */
export interface CasheaIntegrationRequest {
    referenceCode: string;
    status: 'PENDING' | 'NEEDS_REVIEW' | 'ACTIVATED' | 'CANCELLED';
    commerceName: string;
    rif: string;
    products: { qr: boolean; link: boolean };
    fulfilled: { qr: string | null; link: string | null };
    createdAt: string;
}

const casheaLinkShareMessage = (order: CasheaLinkShareable): string => {
    const inicial = order.downPayment !== null && order.downPayment !== undefined ? ` de $${order.downPayment.toFixed(2)}` : '';
    // Falta la inicial solo cuando el cliente ya eligió pagarla en la tienda.
    if (order.orderStatus === 'PENDING_DOWNPAYMENT') {
        return `Elegiste pagar la inicial${inicial} en la tienda: pasa por aquí y te llevas tu compra.`;
    }
    return `Aquí está tu link para pagar con Cashea. Desde ahí pagas la inicial${inicial} o eliges pagarla aquí en la tienda.`;
};

@Injectable({ providedIn: 'root' })
export class CasheaService {
    private readonly BASE_URL = `${environment.API_URL}/cashea`;

    constructor(private http: HttpClient) {}

    getBoxes$(): Observable<CasheaBox[]> {
        return this.http.get<any>(`${this.BASE_URL}/pos`).pipe(map(res => res?.data?.pointOfSales || []));
    }

    createOrder$(posUUID: string, body: { amount: number; invoiceId: string; identificationNumber: string; employeeId?: string }): Observable<CasheaCreateOrderResponse> {
        return this.http.post<any>(`${this.BASE_URL}/orders/${posUUID}`, body).pipe(map(res => res?.data));
    }

    getPaymentPlan$(orderId: string): Observable<CasheaPaymentPlanResponse> {
        return this.http.get<any>(`${this.BASE_URL}/orders/${orderId}/payment-plan`).pipe(map(res => res?.data));
    }

    submitSecurityCode$(orderId: string, userCode: string): Observable<CasheaPaymentPlanResponse> {
        return this.http.post<any>(`${this.BASE_URL}/orders/${orderId}/payment-plan`, { userCode }).pipe(map(res => res?.data));
    }

    // `orderKind` viaja en cada operación sobre una orden ya creada porque el QR
    // y Link no comparten endpoints: sin él el backend consultaría la del QR.
    // Las pantallas del checkout con QR lo omiten y se resuelve como QR.
    getOrderDetails$(orderId: string, orderKind: CasheaOrderKind = 'QR'): Observable<CasheaOrderDetails> {
        return this.http.get<any>(`${this.BASE_URL}/orders/${orderId}/details`, { params: { orderKind } }).pipe(map(res => res?.data));
    }

    cancelOrder$(orderId: string, orderKind: CasheaOrderKind = 'QR'): Observable<any> {
        return this.http.delete<any>(`${this.BASE_URL}/orders/${orderId}`, { params: { orderKind } }).pipe(map(res => res?.data));
    }

    updateInvoiceNumber$(orderId: string, invoiceId: string, billId?: string, orderKind: CasheaOrderKind = 'QR'): Observable<any> {
        return this.http.patch<any>(`${this.BASE_URL}/orders/${orderId}`, { invoiceId, billId, orderKind }).pipe(map(res => res?.data));
    }

    simulateScanQR$(orderId: string): Observable<any> {
        return this.http.post<any>(`${this.BASE_URL}/orders/${orderId}/scan-qr`, {}).pipe(map(res => res?.data));
    }

    getIntegrationRequest$(): Observable<{ request: CasheaIntegrationRequest | null; prefill: CasheaIntegrationPrefill; askContractTerms: boolean }> {
        return this.http.get<any>(`${this.BASE_URL}/integration-requests/current`).pipe(map(res => res?.data));
    }

    createIntegrationRequest$(
        body: { commerceName: string; rif: string; products: { qr: boolean; link: boolean }; comment?: string } & Partial<CasheaContractTermsInput>,
    ): Observable<CasheaIntegrationRequest> {
        return this.http.post<any>(`${this.BASE_URL}/integration-requests`, body).pipe(map(res => res?.data));
    }

    // Unlike the rest of this service, the sync endpoints return their payload
    // unwrapped (no { data } envelope), so we intentionally do not map res.data here.
    startSync$(): Observable<{ status: string }> {
        return this.http.post<{ status: string }>(`${this.BASE_URL}/sync-pending-accounts`, {});
    }

    getSyncStatus$(): Observable<{ inProgress: boolean; lastSyncAt: string | null; lastSyncResult: { synced: number; updated: number; failed: number; error?: string } | null }> {
        return this.http.get<any>(`${this.BASE_URL}/sync-status`);
    }

    /** Ventas incluidas en un pago agregado de Cashea, para el detalle del movimiento. */
    getPayoutItems$(registryId: string): Observable<CasheaPayoutItem[]> {
        return this.http.get<any>(`${this.BASE_URL}/payouts/${registryId}/items`).pipe(map(res => res?.data?.items || []));
    }

    /* ------------------------------------------------------------ *
     * Cashea Link (pago remoto)
     * ------------------------------------------------------------ */

    /** Simula el plan antes de generar el link, para mostrárselo al cliente. */
    getLinkPaymentPlan$(identificationNumber: string, amount: number, orderType: 'DAILY' | 'SPLIT' = 'SPLIT'): Observable<CasheaLinkPlan> {
        return this.http.get<any>(`${this.BASE_URL}/link/payment-plan`, { params: { identificationNumber, amount: String(amount), orderType } }).pipe(map(res => res?.data));
    }

    /**
     * Engancha la orden a la venta abierta que el cajero ya tiene armada: el
     * monto es el total del carrito, no un número que alguien tipea.
     */
    createLinkOrder$(body: {
        saleId: string;
        identificationNumber: string;
        amount: number;
        /** Subtotal sin impuestos del carrito, para que la API lo compare con el guardado. */
        subtotal?: number;
        customerName?: string;
        customer?: CasheaLinkCustomer;
        orderType?: 'DAILY' | 'SPLIT';
    }): Observable<CasheaLinkCreated> {
        return this.http.post<any>(`${this.BASE_URL}/link/orders`, body).pipe(map(res => res?.data));
    }

    /**
     * Las órdenes de la vista. Con `sync`, la API además le consulta a Cashea las
     * pendientes que no se miraron hace un rato, para que el cajero vea el
     * cambio sin tocar nada.
     */
    getLinkOrders$(options: { sync?: boolean } = {}): Observable<CasheaLinkOrders> {
        const params = options.sync ? { sync: '1' } : undefined;
        return this.http.get<any>(`${this.BASE_URL}/link/orders`, { params }).pipe(map(res => ({ orders: res?.data || [] })));
    }

    /** Cuántas órdenes están listas para finalizar, para el contador del menú. */
    getLinkOrdersSummary$(): Observable<{ ready: number }> {
        return this.http.get<any>(`${this.BASE_URL}/link/orders/summary`).pipe(map(res => ({ ready: Number(res?.data?.ready) || 0 })));
    }

    /** Le pregunta a Cashea en el momento; devuelve el estado y la orden ya actualizada. */
    refreshLinkOrder$(saleId: string): Observable<{ orderStatus: CasheaLinkStatus; changed: boolean; sale?: { status: string; casheaLink: any } }> {
        return this.http.post<any>(`${this.BASE_URL}/link/orders/${saleId}/refresh`, {}).pipe(map(res => res?.data));
    }

    /** Confirma la inicial que el comercio cobró en caja ("acordar con aliado"). */
    confirmLinkDownPayment$(saleId: string, amount?: number, paymentMethod?: string): Observable<{ orderStatus: CasheaLinkStatus }> {
        return this.http.post<any>(`${this.BASE_URL}/link/orders/${saleId}/down-payment`, { amount, paymentMethod }).pipe(map(res => res?.data));
    }

    /** Convierte una orden cancelada en venta abierta común, para cobrarla por otro medio. */
    resumeLinkOrder$(saleId: string): Observable<{ saleId: string }> {
        return this.http.post<any>(`${this.BASE_URL}/link/orders/${saleId}/resume`, {}).pipe(map(res => res?.data));
    }

    /**
     * Devuelve a cancelada una orden que se abrió para cobrar por otro medio y
     * no se cobró. Si se cobró o se le generó un link nuevo, la API no hace nada,
     * así que se puede llamar siempre al salir de la venta.
     */
    releaseLinkOrder$(saleId: string): Observable<{ released: boolean }> {
        return this.http.post<any>(`${this.BASE_URL}/link/orders/${saleId}/release`, {}).pipe(map(res => res?.data));
    }

    /**
     * Cancela el link y devuelve la venta a lo que era, para cobrarla de otra
     * forma. La API no lo permite si el cliente ya pagó la inicial.
     */
    unlinkLinkOrder$(saleId: string): Observable<{ saleId: string; name: string; direct: boolean }> {
        return this.http.post<any>(`${this.BASE_URL}/link/orders/${saleId}/unlink`, {}).pipe(map(res => res?.data));
    }

    cancelLinkOrder$(saleId: string, force = false): Observable<any> {
        return this.http.request<any>('delete', `${this.BASE_URL}/link/orders/${saleId}`, { body: { force } });
    }
}
